import { BadRequestException, ForbiddenException, Injectable } from '@nestjs/common';
import { Prisma } from '@prisma/client';
import { PrismaService } from '@cart/api-data-access';
import { StoreContextService } from '../tenancy/store-context.service';
import {
  MERCHANT_CATEGORIES,
  MerchantCategory,
  entityFor,
  prettifyField,
} from './merchant-audit.catalog';

export type MerchantAuditQuery = Record<string, string | undefined>;

export interface MerchantAuditViewer {
  userId: string;
  /** false ⇒ the viewer only sees their own actions. */
  seeEveryone: boolean;
}

export interface MerchantFieldChange {
  field: string;
  label: string;
  from: string | null;
  to: string | null;
}

export interface MerchantAuditRecord {
  id: string;
  category: MerchantCategory;
  entityLabel: string;
  entityName: string;
  operation: string;
  title: string;
  changes: MerchantFieldChange[];
}

const DEFAULT_TAKE = 25;
const MAX_TAKE = 100;

/** Bookkeeping columns a merchant never needs to see. */
const PLUMBING = new Set([
  'id', 'createdOn', 'createdAt', 'createdBy', 'updatedOn', 'updatedAt', 'updatedBy',
  'sortOrder', 'deletedAt', 'deletedBy', 'storeId', 'organizationId', 'isActive',
]);

/**
 * The merchant-facing projection of the change log.
 *
 * Reads the same rows as the platform audit but presents them as business
 * events, restricted to the tables in `merchant-audit.catalog.ts`. Anything the
 * catalogue does not describe is hidden rather than rendered in system
 * vocabulary — see the note there about why absence must mean hidden.
 */
@Injectable()
export class MerchantAuditService {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext: StoreContextService
  ) {}

  /** Fails closed — an audit endpoint must never fall back to a default store. */
  private storeId(): string {
    const context = this.storeContext.current();

    if (!context.resolved || !context.storeId) {
      throw new BadRequestException(
        'Activity history requires a resolved store (send x-store-id).'
      );
    }

    return context.storeId;
  }

  private paging(query: MerchantAuditQuery): { take: number; skip: number } {
    const take = Number(query.take ?? DEFAULT_TAKE);
    const skip = Number(query.skip ?? 0);

    return {
      take: Number.isFinite(take) ? Math.min(Math.max(take, 1), MAX_TAKE) : DEFAULT_TAKE,
      skip: Number.isFinite(skip) ? Math.max(skip, 0) : 0,
    };
  }

  private dateRange(query: MerchantAuditQuery): { gte?: Date; lte?: Date } | undefined {
    const parse = (v?: string) => {
      const d = v ? new Date(v) : undefined;
      return d && !Number.isNaN(d.getTime()) ? d : undefined;
    };

    const gte = parse(query.from);
    const lte = parse(query.to);
    return gte || lte ? { ...(gte ? { gte } : {}), ...(lte ? { lte } : {}) } : undefined;
  }

  /**
   * Activity, grouped by the action that caused it.
   *
   * Reads batch headers (one row per user action) and attaches the records each
   * one touched, so the list is a plain indexed scan rather than an aggregate
   * over the detail table.
   */
  async listActivity(query: MerchantAuditQuery, viewer: MerchantAuditViewer) {
    const storeId = this.storeId();
    const { take, skip } = this.paging(query);
    const range = this.dateRange(query);

    if (query.category && !MERCHANT_CATEGORIES.includes(query.category as MerchantCategory)) {
      throw new BadRequestException(`Unknown category: ${query.category}`);
    }

    // Keyset, not OFFSET. `skip: page * size` re-scans every skipped row, and
    // the COUNT(*) that page numbers need scans the whole matching set on every
    // load — both are O(store history) and get slower every week. A cursor on
    // startedAt is O(page) no matter how deep the reader goes.
    const cursor = this.parseCursor(query.cursor);

    const where: Prisma.ChangeBatchWhereInput = {
      storeId,
      // A batch with no records changed nothing worth showing.
      rowCount: { gt: 0 },
      // Denormalised onto the header by the reconciler, so this narrows the
      // same rows the paging walks.
      ...(query.category ? { category: query.category } : {}),
      // Staff without `audit.view.all` see only what they themselves did.
      ...(viewer.seeEveryone ? {} : { actorId: viewer.userId }),
      ...(query.actorId && viewer.seeEveryone ? { actorId: query.actorId } : {}),
      ...(cursor ? { startedAt: { lt: cursor } } : {}),
      ...(range ? { startedAt: range } : {}),
      ...(query.search
        ? {
            OR: [
              { summary: { contains: query.search, mode: 'insensitive' } },
              { actorName: { contains: query.search, mode: 'insensitive' } },
              { action: { contains: query.search, mode: 'insensitive' } },
            ],
          }
        : {}),
    };

    // One extra row tells us whether another page exists without counting.
    const batches = await this.prisma.changeBatch.findMany({
      where,
      orderBy: { startedAt: 'desc' },
      take: take + 1,
      skip,
    });

    const hasMore = batches.length > take;
    if (hasMore) batches.pop();

    const nextCursor = hasMore ? batches[batches.length - 1]?.startedAt.toISOString() ?? null : null;

    if (!batches.length) return { items: [], hasMore: false, nextCursor: null };

    // One query for every batch on the page, then grouped in memory — N+1
    // would otherwise put one round trip per row on the list view.
    // Fetched by batchId alone. The previous OR of 40 (schema, table) pairs was
    // both hard for the planner and redundant — toRecord() already drops
    // anything the catalogue does not describe.
    const rows = await this.prisma.changeLog.findMany({
      where: { batchId: { in: batches.map((b) => b.batchId) } },
      orderBy: { changedOn: 'asc' },
      take: MAX_TAKE * 10,
    });

    const byBatch = new Map<string, MerchantAuditRecord[]>();
    for (const row of rows) {
      const record = this.toRecord(row);
      if (!record) continue;

      const list = byBatch.get(row.batchId as string) ?? [];
      list.push(record);
      byBatch.set(row.batchId as string, list);
    }

    const items = batches
      .map((batch) => {
        const records = byBatch.get(batch.batchId) ?? [];
        return {
          batchId: batch.batchId,
          occurredAt: batch.startedAt,
          actorName: batch.actorName ?? 'System',
          actorRole: batch.actorRole ?? null,
          actorId: batch.actorId ?? null,
          isSystem: !batch.actorId || batch.actorName === null,
          category: records[0]?.category ?? null,
          title: this.batchTitle(batch.summary, records),
          recordCount: records.length,
          records,
        };
      })
      // A batch whose records are all outside the merchant's allowlist becomes
      // empty here; showing "1 change" with nothing under it would be worse
      // than not showing it.
      .filter((item) => item.recordCount > 0);

    return { items, hasMore, nextCursor };
  }

  /** Cursor is the previous page's last `startedAt`. Invalid input pages from the top. */
  private parseCursor(cursor?: string): Date | undefined {
    if (!cursor) return undefined;

    const parsed = new Date(cursor);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  /** "Everything that ever happened to this product / this order." */
  async recordHistory(query: MerchantAuditQuery, viewer: MerchantAuditViewer) {
    const storeId = this.storeId();
    const { take, skip } = this.paging(query);

    if (!query.tableName || !query.recordId) {
      throw new BadRequestException('tableName and recordId are required');
    }

    const entity = entityFor(query.schemaName ?? '', query.tableName);
    if (!entity) throw new ForbiddenException('That record type has no merchant history.');

    const where: Prisma.ChangeLogWhereInput = {
      storeId,
      schemaName: query.schemaName,
      tableName: query.tableName,
      recordId: query.recordId,
      // Own-actions-only viewers are filtered in SQL, so the page size means
      // what it says rather than being thinned out after the fact.
      ...(viewer.seeEveryone ? {} : { changedBy: viewer.userId }),
    };

    const [rows, total] = await Promise.all([
      this.prisma.changeLog.findMany({ where, orderBy: { changedOn: 'desc' }, take, skip }),
      this.prisma.changeLog.count({ where }),
    ]);

    const items = rows
      .map((row) => {
        const record = this.toRecord(row);
        if (!record) return null;

        return {
          ...record,
          occurredAt: row.changedOn,
          actorName: row.actorName ?? row.changedBy ?? 'System',
          actorRole: row.actorRole ?? null,
          batchId: row.batchId,
        };
      })
      .filter((i): i is NonNullable<typeof i> => i !== null);

    return { items, total };
  }

  /** Team members who appear in this store's history, for the actor filter. */
  async actors() {
    const storeId = this.storeId();

    const grouped = await this.prisma.changeBatch.groupBy({
      by: ['actorId', 'actorName'],
      where: { storeId, actorId: { not: null }, rowCount: { gt: 0 } },
      _count: { _all: true },
      orderBy: { _count: { actorId: 'desc' } },
      take: 25,
    });

    return grouped.map((g) => ({
      actorId: g.actorId,
      actorName: g.actorName,
      count: g._count._all,
    }));
  }

  async summary() {
    const storeId = this.storeId();
    const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

    const [actions, people] = await Promise.all([
      this.prisma.changeBatch.count({ where: { storeId, rowCount: { gt: 0 }, startedAt: { gte: since } } }),
      this.prisma.changeBatch.groupBy({
        by: ['actorId'],
        where: { storeId, rowCount: { gt: 0 }, startedAt: { gte: since }, actorId: { not: null } },
      }),
    ]);

    return { actions, people: people.length, windowDays: 7, categories: MERCHANT_CATEGORIES };
  }

  // ── Projection ────────────────────────────────────────────────────────

  private toRecord(row: {
    id: string;
    schemaName: string;
    tableName: string;
    recordId: string;
    operation: string;
    changedColumns: unknown;
    oldValue: unknown;
    newValue: unknown;
  }): MerchantAuditRecord | null {
    const entity = entityFor(row.schemaName, row.tableName);
    if (!entity) return null; // not in the catalogue ⇒ not a merchant's business

    const oldValue = (row.oldValue ?? {}) as Record<string, unknown>;
    const newValue = (row.newValue ?? {}) as Record<string, unknown>;
    const columns = Array.isArray(row.changedColumns) ? (row.changedColumns as string[]) : [];

    const entityName = this.nameOf(entity.nameFields, newValue, oldValue, row.recordId);

    const changes = columns
      .filter((c) => !PLUMBING.has(c) && !entity.hidden?.includes(c))
      // Foreign keys are internal plumbing: "Inventory item id: → 5572f795…"
      // tells a shop owner nothing. They are shown only where the catalogue
      // gives them a deliberate label (brandId → "Brand"), and even then the
      // value is shortened rather than printed as a raw uuid.
      .filter((c) => !/Id$/.test(c) || Boolean(entity.fields?.[c]))
      .map((field) => ({
        field,
        label: entity.fields?.[field] ?? prettifyField(field),
        from: this.format(field, oldValue[field], entity.money),
        to: this.format(field, newValue[field], entity.money),
      }))
      .filter((c) => c.from !== null || c.to !== null);

    return {
      id: row.id,
      category: entity.category,
      entityLabel: entity.label,
      entityName,
      operation: row.operation,
      title: this.recordTitle(entity.label, entityName, row.operation, changes),
      changes,
    };
  }

  private nameOf(
    nameFields: string[],
    newValue: Record<string, unknown>,
    oldValue: Record<string, unknown>,
    recordId: string
  ): string {
    for (const field of nameFields) {
      // Taken from the row's own snapshot, not a join — so it still resolves
      // for a record that has since been deleted, which is exactly when the
      // history matters most.
      const value = newValue[field] ?? oldValue[field];
      if (typeof value === 'string' && value.trim()) return value.trim();
      if (typeof value === 'number') return String(value);
    }

    return `#${recordId.slice(0, 8)}`;
  }

  private recordTitle(
    label: string,
    name: string,
    operation: string,
    changes: MerchantFieldChange[]
  ): string {
    if (operation === 'create') return `${label} created — ${name}`;
    if (operation === 'delete') return `${label} deleted — ${name}`;

    // Naming the field reads far better than "Product updated" when only one
    // thing changed, which is the common case.
    if (changes.length === 1) return `${changes[0].label} changed on ${name}`;
    return `${label} updated — ${name}`;
  }

  private batchTitle(summary: string | null, records: MerchantAuditRecord[]): string {
    if (records.length === 1) return records[0].title;
    if (summary) return summary;

    const label = records[0]?.entityLabel ?? 'Records';
    return `${label} updated — ${records.length} records`;
  }

  /** Values a merchant reads: rupees, Yes/No, dates, never a raw object. */
  private format(field: string, value: unknown, money?: string[]): string | null {
    if (value === undefined) return null;
    if (value === null) return '—';
    if (value === '[REDACTED]') return '•••';

    if (money?.includes(field) && typeof value === 'number') {
      return `₹${(value / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
    }

    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'number') return String(value);

    if (typeof value === 'string') {
      // ISO timestamps read badly raw.
      if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString('en-IN');
      }

      // A uuid is a reference, not a value worth reading in full.
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) {
        return `#${value.slice(0, 8)}`;
      }

      return value.length > 160 ? `${value.slice(0, 160)}…` : value;
    }

    if (Array.isArray(value)) return `${value.length} items`;
    return '—';
  }
}
